import { Radio, Select } from 'antd';
import React from 'react';
import { IAnalytcisUserInput } from '../../../types/IAnalyticsUserInput';
import { IMarker } from '../../../types/marker';
import { UserGroupPermissions } from '../../../types/userGroupPermissions';
import { ValidationTabsEnum } from '../../../types/validation';
import {
  DownloadCheckboxKey,
  ROSE_PLOT_VALIDATION,
  SCATTER_PLOT_VALIDATION,
  TabKey,
  TS_PLOT_VALIDATION,
} from '../../../utils/common';
import AnalyticsLayout from '../Analytics/components/Layout/AnalyticsLayout';
import styles from '../ResultPanel/Style/ValidationPanel.module.scss';
import InSituTab from './components/Tabs/InSituTab';
import SatelliteTab from './components/Tabs/SatelliteTab';

interface Props {
  userGroupPermissions: UserGroupPermissions;
  datasetId: string;
  category: string;
  cellSize: string;
  waterDepth: string;
  activePoint?: IMarker;
  activePlotValidation: string[];
  setActivePlotValidation: React.Dispatch<React.SetStateAction<string[]>>;
  onChangePointLocation: (coords: number[], forceChange?: boolean) => void;
  onMeshControl: (item: string) => void;
  onChangeActiveKey: (item: any, tabKey: TabKey) => void;
  userInput: IAnalytcisUserInput;
  updateUserInput: (update: Partial<IAnalytcisUserInput>) => void;
  setDatasetIsOfflineWarningVisible: (visible: boolean) => void;
  validationDeps: {
    isLoadingExtraction: boolean;
    advancedAnalytics: any;
    activeValidationTab: string;
    handleChangeTab: (tab: ValidationTabsEnum) => void;
    isInsituAvailable: boolean;
    disabledChart: boolean;
    activeInsituTab: number;
    radioGroupOnChange: (e: any) => void;
    radioGroupValue: number;
    isTimeSeriesDisabled: boolean;
    isScatterPlotDisabled: boolean;
    isRosePlotDisabled: boolean;
    pointOptions: any[];
    handleChangePoint: (value: any) => void;
  };
}

const ValidationPanel: React.FC<Props> = ({
  activePoint,
  cellSize,
  waterDepth,
  userGroupPermissions,
  datasetId,
  onChangePointLocation,
  onMeshControl,
  activePlotValidation,
  setActivePlotValidation,
  validationDeps,
  userInput,
  updateUserInput,
  setDatasetIsOfflineWarningVisible,
}) => {
  const {
    activeValidationTab,
    handleChangeTab,
    isInsituAvailable,
    disabledChart,
    activeInsituTab,
    radioGroupOnChange,
    radioGroupValue,
    isTimeSeriesDisabled,
    isScatterPlotDisabled,
    isRosePlotDisabled,
    pointOptions,
    handleChangePoint,
  } = validationDeps;

  const containerTabStyle = (isActive: boolean) => ({
    padding: '12px 8px',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    borderRadius: '6px',
    margin: '2px 0',
    cursor: 'pointer',
    color: '#0099ff',
    fontWeight: isActive ? 'bold' : 'normal',
    backgroundColor: isActive ? 'rgba(0, 153, 255, 0.1)' : 'transparent',
    borderRight: isActive ? '3px solid #0099ff' : 'none',
    transform: isActive ? 'translateX(2px)' : 'none',
    opacity: isActive ? 1 : 0.6,
  });

  const textTabStyle = (isActive: boolean) => ({
    fontSize: '13px',
    display: 'block',
    lineHeight: '1.3',
    color: 'inherit',
    fontWeight: isActive ? 600 : 'normal',
  });

  return (
    <AnalyticsLayout
      globalContent={
        <div style={{ padding: '8px', paddingTop: 0 }}>
          <div
            style={{
              fontSize: '14px',
              fontWeight: 500,
              marginBottom: '6px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span>Point</span>
          </div>
          <Select
            id='ValidationPanel-Select_Point'
            style={{ width: '100%', height: '30px' }}
            placeholder='Select point'
            onChange={handleChangePoint}
            options={pointOptions}
            value={activePoint}
          />
        </div>
      }
      navigationContent={
        <>
          <div className={styles.listNavigation}>
            <div
              role='button'
              style={containerTabStyle(
                activeValidationTab === ValidationTabsEnum.SATELLITE_TAB
              )}
              key={ValidationTabsEnum.SATELLITE_TAB}
              onClick={() => handleChangeTab(ValidationTabsEnum.SATELLITE_TAB)}
              id='Altimeter-tab'
            >
              <span
                style={textTabStyle(
                  activeValidationTab === ValidationTabsEnum.SATELLITE_TAB
                )}
              >
                Altimeter
              </span>
            </div>
            <div
              role='button'
              style={containerTabStyle(
                activeValidationTab === ValidationTabsEnum.INSITU_TAB
              )}
              key={ValidationTabsEnum.INSITU_TAB}
              onClick={() => handleChangeTab(ValidationTabsEnum.INSITU_TAB)}
              id='In-situ-tab'
            >
              <span
                style={textTabStyle(
                  activeValidationTab === ValidationTabsEnum.INSITU_TAB
                )}
              >
                In situ
              </span>
            </div>
          </div>
          <div
            style={{
              borderBottom: '2px solid #f0f0f0',
              width: '95%',
              marginTop: 10,
              marginBottom: 10,
            }}
          />
          <Radio.Group
            onChange={radioGroupOnChange}
            value={radioGroupValue}
            size='small'
            style={{
              paddingLeft: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <Radio
              disabled={isTimeSeriesDisabled}
              value={TS_PLOT_VALIDATION}
              data-testid='Validation-Panel__ScatterPlot-timeseries-radio'
            >
              Time Series Plot
            </Radio>
            <Radio
              disabled={isScatterPlotDisabled}
              value={SCATTER_PLOT_VALIDATION}
              data-testid='Validation-Panel__ScatterPlot-radio'
            >
              Scatter Plot
            </Radio>
            <Radio
              disabled={isRosePlotDisabled}
              value={ROSE_PLOT_VALIDATION}
              data-testid='Validation-Panel__RosePlot-radio'
            >
              Rose Plot
            </Radio>
          </Radio.Group>
        </>
      }
    >
      <div
        style={{
          display:
            activeValidationTab === ValidationTabsEnum.INSITU_TAB
              ? 'block'
              : 'none',
          height: '100%',
        }}
      >
        <InSituTab
          cellSize={cellSize}
          datasetId={datasetId}
          userGroupPermissions={userGroupPermissions}
          waterDepth={waterDepth}
          activePoint={activePoint}
          validInSituPoint={disabledChart}
          isInsituAvailable={isInsituAvailable}
          onChangePointLocation={onChangePointLocation}
          activePanel={activeValidationTab}
          activeInsituTab={activeInsituTab}
          onMeshControl={onMeshControl}
          setDatasetIsOfflineWarningVisible={setDatasetIsOfflineWarningVisible}
        />
      </div>
      <div
        style={{
          display:
            activeValidationTab === ValidationTabsEnum.SATELLITE_TAB
              ? 'block'
              : 'none',
          height: '100%',
        }}
      >
        <SatelliteTab
          advancedAnalytics={validationDeps.advancedAnalytics}
          activePointId={activePoint?.id!}
          datasetKey={activePoint?.activeDatasetKey ?? DownloadCheckboxKey.SW}
          downloadItems={activePoint?.downloadPage!}
          coordinates={activePoint?.coordinates!}
          previewDatasetId={datasetId ?? ''}
          userGroupPermissions={userGroupPermissions!}
          pointName={activePoint?.label ?? ''}
          gridCellSize={cellSize}
          waterDepth={waterDepth}
          activePlot={activePlotValidation}
          setActivePlot={setActivePlotValidation}
          activePanel={activeValidationTab}
          userInput={userInput}
          updateUserInput={updateUserInput}
          isLoadingExtraction={validationDeps.isLoadingExtraction}
          setDatasetIsOfflineWarningVisible={setDatasetIsOfflineWarningVisible}
        />
      </div>
    </AnalyticsLayout>
  );
};

export default ValidationPanel;
